import { useState, useEffect,useRef } from 'react';
import NotificationOverview from './NotificationOverview';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  TextField,
  Grid,
  IconButton,
  SelectChangeEvent
} from '@mui/material';
import { Trash } from 'iconsax-react';
import { Formik, Form, Field, FieldArray } from 'formik';
import useApi from 'hooks/useApi';
import { PERMISSIONS, useHasPermission } from 'contexts/PermissionContext';
// import NotificationOverview from './NotificationOverview';

interface NotificationCondition {
  type: 'eta' | 'field_change' | 'completion';
  field?: string;
  operator?: '+' | '-' | '±';
  value?: number;
  valueType?: 'minutes' | 'hours' | 'days';
  fieldName?: string;
  fieldOperator?: '=' | '!=' | '>' | '<' | 'contains' | 'on change';
  fieldValue?: string;
}

export enum NotificationConditionType {
  ON_COMPLETE = 'ON_COMPLETE',
  ON_ETA = 'ON_ETA',
  ON_FIELD_CHANGE = 'ON_FIELD_CHANGE'
}

export enum NotificationChannel {
  EMAIL = 'EMAIL',
  IN_APP = 'IN-APP',
  SMS = 'SMS',
  PRIORITY = 'PRIORITY',
  ALERT = 'ALERT'
}

interface TeamNotificationSettingsProps {
  teamUid: string;
  clientUID?: string;

}

const TeamNotificationSettings = ({clientUID, teamUid  }: TeamNotificationSettingsProps) => {
  const hasNotificationPermission = useHasPermission(PERMISSIONS.ADD_EDIT_NOTIFICATIONS);
  
  const defaultSettings: {
    name: string;
    channels: string[];
    isPriority: boolean;
    roles: string[];
    users: string[];
    conditions: NotificationCondition[];
  } = {
    name: '',
    channels: [],
    isPriority: false,
    roles: [],
    users: [],
    conditions: []
  };
const [editingNotification, setEditingNotification] = useState<any>(null);
const [notificationRefresh, setNotificationRefresh] = useState(0);
  const [initialValues, setInitialValues] = useState(defaultSettings);
  const [fieldOptions, setFieldOptions] = useState<any[]>([]);
  const [rolesList, setRolesList] = useState<any[]>([]);
  const [usersList, setusersList] = useState<{ uid: string; name: string }[]>([]);
  const formRef = useRef<HTMLDivElement>(null);
  const { getFlowByTeamUid, getTeamUsers, saveTeamNotifications, updateTeamNotification, showToast, getResourceTypes } = useApi();

  useEffect(() => {
    if (teamUid) fetchTeamData();
  }, [teamUid]);

  useEffect(() => {
    if (clientUID) fetchusers(clientUID);
  }, [clientUID]);

  useEffect(() => {
    if (clientUID) fetchRoles(clientUID);
  }, [clientUID]);

  const fetchusers = async (clientUid: string) => {
    try {
      const userList = await getTeamUsers(teamUid);
      if (Array.isArray(userList.data)) {
        const processedUsers = userList.data
          .map((entry: any) => {
            let name = '';
            if (entry.user) {
              const fullName = `${entry.user.firstName || ''} ${entry.user.lastName || ''}`.trim();
              name = fullName || entry.user.email || entry.user.username || '';
            } else if (entry.firstName || entry.lastName) {
              name = `${entry.firstName || ''} ${entry.lastName || ''}`.trim() || entry.email || entry.username || '';
            } else if (entry.name) {
              name = entry.name;
            } else if (entry.email) {
              name = entry.email;
            } else if (entry.username) {
              name = entry.username;
            }
            return {
              uid:  entry.user.uid,
              name: name
            };
          })
          .filter((user: any) => user.name && user.name.trim() !== '' && user.name !== 'Unnamed User');
        setusersList(processedUsers);
      }
    } catch (error) {
      console.error('Error fetching client users:', error);
    }
  };

  const fetchRoles = async (clientUID: string) => {
    try {
      const response = await getResourceTypes(clientUID, 'user');
      if (Array.isArray(response?.data)) {
        const processedRoles = response.data.map((role: any) => ({ uid: role.uid, name: role.name }));
        setRolesList(processedRoles);
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (editingNotification) {
      setInitialValues({
        name: editingNotification.name || '',
        channels: (editingNotification.channels || []).map((c: string) => {
          if (c === 'EMAIL') return 'email';
          if (c === 'IN-APP') return 'in-app';
          if (c === 'SMS') return 'sms';
          if (c === 'PRIORITY') return 'priority';
          return c;
        }),
        isPriority: false,
        roles: editingNotification.roles || [],
        users: editingNotification.users || [],
        conditions: [
          {
            type: editingNotification.conditions?.[0]?.type || 'completion',
            field: editingNotification.conditions?.[0]?.field || '',
            operator: editingNotification.conditions?.[0]?.operator || '-',
            value: editingNotification.conditions?.[0]?.value || 15,
            valueType: editingNotification.conditions?.[0]?.valueType || 'minutes'
          }
        ]
      });
    }
  }, [editingNotification]);

  const fetchTeamData = async () => {
    try {
      const flowResp = await getFlowByTeamUid(teamUid);
      if (flowResp?.data?.steps) {
        setFieldOptions(flowResp.data.steps);
      }
    } catch (error) {
      console.error('Error fetching team data:', error);
    }
  };

  // Helper: Map UI state to API
  const mapToApiPayload = (form: any) => {
    const channelMap: Record<string, NotificationChannel> = {
      email: NotificationChannel.EMAIL,
      'in-app': NotificationChannel.IN_APP,
      sms: NotificationChannel.SMS,
      priority: NotificationChannel.PRIORITY,
      alert: NotificationChannel.ALERT
    };

    return {
      users: form.users,
      roles: form.roles,
      name: form.name,
      conditions: form?.conditions,
      channels: form.channels.map((c: string) => channelMap[c] || c)
    };
  };

  // Add trigger condition builder
  function getNewCondition(type: 'eta' | 'field_change' | 'completion') {
    if (type === 'eta') {
      return {
        type,
        field: '',
        operator: '+',
        value: 15,
        valueType: 'minutes'
      };
    }
    if (type === 'field_change') {
      return {
        type,
        fieldName: '',
        fieldOperator: '=',
        fieldValue: ''
      };
    }
    return { type: 'completion' };
  }
  // console.log("fieldOptions", fieldOptions);
  // Patch form values when editingNotification changes
  useEffect(() => {
    if (editingNotification) {
      setInitialValues({
        channels: (editingNotification.channels || []).map((c: string) => {
          // Map API channel values to form values
          if (c === 'EMAIL') return 'email';
          if (c === 'IN-APP') return 'in-app';
          if (c === 'SMS') return 'sms';
          if (c === 'PRIORITY') return 'priority';
          return c;
        }),
        isPriority: false,
        roles: editingNotification.roles || [],
        users: editingNotification.users || [],
        name: editingNotification.name || '',
        conditions: editingNotification.conditions || [],
      });
    }
  }, [editingNotification]);
  const handleEdit = (notification: any) => {
    setEditingNotification(notification);
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100); // Delay to ensure Formik re-renders
  };
  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Notification Settings
        </Typography>
      </Box>

      <NotificationOverview
        key={notificationRefresh}
        rolesList={rolesList}
        teamUid={teamUid}
        usersList={usersList}
        onEdit={handleEdit}
        disabled={!hasNotificationPermission}
      />
      <div ref={formRef}>
      <Formik 
        enableReinitialize
        initialValues={initialValues}
        onSubmit={async (values, { setSubmitting, resetForm }) => {
          console.log("priority transactions", values);
          try {
            if (editingNotification && editingNotification.uid) {
              await updateTeamNotification(teamUid, editingNotification.uid, mapToApiPayload(values));
              setEditingNotification(null);
              setInitialValues(defaultSettings);
              showToast('Notification updated successfully', 'success');
              resetForm();
            } else {
              await saveTeamNotifications(teamUid, mapToApiPayload(values));
              showToast('Notification settings saved successfully', 'success');
              resetForm();
            }
            setNotificationRefresh((prev) => prev + 1);
          } catch (error) {
            showToast('Failed to save notification settings', 'error');
            console.error(error); /* Optionally show error */
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ values, handleChange, setFieldValue, isSubmitting }) => (
          <Form>
            <Box
              sx={{
                mb: 4,
                border: '1px solid #eee',
                borderRadius: 2,
                p: 2,
                position: 'relative'
              }}
            >
              <Card sx={{ boxShadow: 'none', border: 'none' }}>
                <CardContent>

                  {/* Notification Name Field */}
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6} md={5} lg={4} xl={3}>
                      <Typography variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
                        Notification Name
                      </Typography>
                      <TextField
                        name="name"
                        value={values.name}
                        onChange={handleChange}
                        fullWidth
                        size="small"
                        placeholder="Enter notification name"
                      />
                    </Grid>
                  </Grid>

                   <Box sx={{ mt: 4 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
                      Conditional Trigger
                    </Typography>
                    <FieldArray
                      name="conditions"
                      render={(arrayHelpers) => (
                        <>
                          <FormControl fullWidth size="small" sx={{ mb: 3 }}>
                            <InputLabel id="trigger-select-label-0">Select Trigger</InputLabel>
                            <Select
                              labelId="trigger-select-label-0"
                              value={values.conditions[0]?.type || ''}
                              onChange={(e) => {
                                arrayHelpers.remove(0);
                                arrayHelpers.insert(0, getNewCondition(e.target.value as 'eta' | 'field_change' | 'completion'));
                              }}
                              label="Select Trigger"
                            >
                              <MenuItem value="eta">On ETA Trigger</MenuItem>
                              <MenuItem value="field_change">On Field Value Change</MenuItem>
                              <MenuItem value="completion">On Completion</MenuItem>
                            </Select>
                          </FormControl>

                          {values.conditions[0]?.type === 'eta' && (
                            <Grid container spacing={2} key="eta" sx={{ mb: 1 }} alignItems="center">
                              {/* Steps Dropdown (same as completion) */}
                              <Grid item xs={10}>
                                <FormControl fullWidth size="small">
                                  <InputLabel>Select Step</InputLabel>
                                  <Select
                                    value={values.conditions[0].fieldName || ''}
                                    onChange={(e) => setFieldValue('conditions[0].fieldName', e.target.value)}
                                  >
                                    {Array.isArray(fieldOptions) &&
                                      fieldOptions.map((step, idx2) => (
                                        <MenuItem key={step.title + idx2} value={step.title}>
                                          {step.title}
                                        </MenuItem>
                                      ))}
                                  </Select>
                                </FormControl>
                              </Grid>
                              {/* ETA Field and related inputs */}
                              <Grid item xs={3}>
                                <FormControl fullWidth size="small">
                                  <InputLabel htmlFor="eta-field-native-select">ETA Field</InputLabel>
                                  <Select
                                    native
                                    value={values.conditions[0].field || ''}
                                    onChange={(e) => setFieldValue('conditions[0].field', e.target.value)}
                                    inputProps={{
                                      name: 'etaField',
                                      id: 'eta-field-native-select'
                                    }}
                                  >
                                    <option aria-label="None" value="" />
                                    {Array.isArray(fieldOptions) &&
                                      fieldOptions.map((step: any, idx2: number) => {
                                        const dueDateFields = step.component?.fields?.filter((f: any) => f?.inputComponent==="DATE") || [];
                                        if (dueDateFields.length === 0) return null;
                                        return (
                                          <optgroup
                                            key={step.title + idx2}
                                            label={step.title}
                                            style={{
                                              fontFamily: "'Inter var', -apple-system, BlinkMacSystemFont, sans-serif",
                                              fontWeight: 600,
                                              fontSize: '0.9rem',
                                              color: '#444',
                                              // backgroundColor: "#f1f3f6",
                                              padding: '6px 10px'
                                            }}
                                          >
                                            {dueDateFields.map((field: any) => (
                                              <option
                                                key={field._id}
                                                value={field.question}
                                                style={{
                                                  fontFamily: "'Inter var', -apple-system, BlinkMacSystemFont, sans-serif",
                                                  fontSize: '0.9rem',
                                                  padding: '8px 10px',
                                                  color: '#333',
                                                  backgroundColor: '#fff'
                                                }}
                                              >
                                                {field.question}
                                              </option>
                                            ))}
                                          </optgroup>
                                        );
                                      })}
                                  </Select>
                                </FormControl>
                              </Grid>
                              <Grid item xs={2}>
                                <FormControl fullWidth size="small">
                                  <Select
                                    value={values.conditions[0].operator || '+'}
                                    onChange={(e) => setFieldValue('conditions[0].operator', e.target.value)}
                                  >
                                    <MenuItem value="add">+</MenuItem>
                                    <MenuItem value="subtract">-</MenuItem>
                                    {/* <MenuItem value="±">±</MenuItem> */}
                                  </Select>
                                </FormControl>
                              </Grid>
                              <Grid item xs={2}>
                                <Field as={TextField} fullWidth name="conditions[0].value" type="number" size="small" />
                              </Grid>
                              <Grid item xs={3}>
                                <FormControl fullWidth size="small">
                                  <Select
                                    value={values.conditions[0].valueType || 'minutes'}
                                    onChange={(e) => setFieldValue('conditions[0].valueType', e.target.value)}
                                  >
                                    <MenuItem value="minutes">Minutes</MenuItem>
                                    <MenuItem value="hours">Hours</MenuItem>
                                    <MenuItem value="days">Days</MenuItem>
                                  </Select>
                                </FormControl>
                              </Grid>
                              <Grid item xs={2}>
                                <IconButton color="error" onClick={() => arrayHelpers.remove(0)}>
                                  <Trash />
                                </IconButton>
                              </Grid>
                            </Grid>
                          )}

                          {values.conditions[0]?.type === 'field_change' && (
                            <Grid container spacing={2} key="fieldchange" sx={{ mb: 1, alignItems: 'center' }}>
                              <Grid item xs={3}>
                                {' '}
                                {/* Adjusted width for better spacing */}
                                <FormControl fullWidth size="small">
                                  <InputLabel htmlFor="field-name-native-select">Field Name</InputLabel>
                                  <Select
                                    native // Use native select
                                    value={values.conditions[0].fieldName || ''}
                                    onChange={(e) => setFieldValue('conditions[0].fieldName', e.target.value)}
                                    inputProps={{
                                      name: 'fieldName',
                                      id: 'field-name-native-select'
                                    }}
                                  >
                                    <option aria-label="None" value="" />
                                    {Array.isArray(fieldOptions) &&
                                      fieldOptions.map((step, idx2) => {
                                        const allFields = step.component?.fields || [];
                                        if (allFields.length === 0) return null;
                                        return (
                                          <optgroup key={step.title + idx2} label={step.title}>
                                            {allFields.map((field: any) => (
                                              <option
                                                key={field._id} // Use _id for key and value
                                                value={field.question}
                                                style={{
                                                  fontFamily: "'Inter var', -apple-system, BlinkMacSystemFont, sans-serif",
                                                  fontSize: '0.9rem',
                                                  padding: '8px 10px',
                                                  color: '#333',
                                                  backgroundColor: '#fff'
                                                }}
                                              >
                                                {field.question}
                                              </option>
                                            ))}
                                          </optgroup>
                                        );
                                      })}
                                  </Select>
                                </FormControl>
                              </Grid>
                              <Grid item xs={3}>
                                <FormControl fullWidth size="small">
                                  <Select
                                    value={values.conditions[0].fieldOperator || '='}
                                    onChange={(e) => setFieldValue('conditions[0].fieldOperator', e.target.value)}
                                  >
                                    <MenuItem value="equal">=</MenuItem>
                                    <MenuItem value="not_equal">!=</MenuItem>
                                    <MenuItem value="contains">contains</MenuItem>
                                    <MenuItem value="on change">on Changed</MenuItem>
                                  </Select>
                                </FormControl>
                              </Grid>
                              {values.conditions[0].fieldOperator !== 'on change' && (
                                <Grid item xs={3}>
                                  <TextField
                                    fullWidth
                                    name="conditions[0].fieldValue"
                                    placeholder="Value"
                                    size="small"
                                    value={values.conditions[0].fieldValue || ''}
                                    onChange={handleChange} 
                                  />
                                </Grid>
                              )}
                              <Grid item xs={1}>
                                <IconButton
                                  color="error"
                                  onClick={() => {
                                    setFieldValue('conditions', []); 
                                  }}
                                >
                                  <Trash />
                                </IconButton>
                              </Grid>
                            </Grid>
                          )}
                          {values.conditions[0]?.type === 'completion' && (
                            <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
                              <Grid item xs={10}>
                                <FormControl fullWidth size="small">
                                  <InputLabel>Select Step</InputLabel>
                                  <Select
                                    value={values.conditions[0].fieldName || ''}
                                    onChange={(e) => setFieldValue('conditions[0].fieldName', e.target.value)}
                                  >
                                    {Array.isArray(fieldOptions) &&
                                      fieldOptions.map((step, idx2) => (
                                        <MenuItem key={step.title + idx2} value={step.title}>
                                          {step.title}
                                        </MenuItem>
                                      ))}
                                  </Select>
                                </FormControl>
                              </Grid>
                              <Grid item xs={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <IconButton color="error" onClick={() => arrayHelpers.remove(0)}>
                                  <Trash />
                                </IconButton>
                              </Grid>
                            </Grid>
                          )}
                        </>
                      )}
                    />
                  </Box>  
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
                    Channel (Method to Send)
                  </Typography>
                  <FormControl fullWidth size="small" sx={{ mb: 3 }}>
                    <InputLabel id="channel-select-label-0">Select Channels</InputLabel>
                    <Field
                      name="channels"
                      as={Select}
                      labelId="channel-select-label-0"
                      multiple
                      value={values.channels}
                      onChange={(e: any) => setFieldValue('channels', e.target.value)}
                      label="Select Channels"
                      renderValue={(selected: string[]) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => {
                            let label = '';
                            if (value === 'in-app') label = 'In-App';
                            else if (value === 'email') label = 'Email';
                            else if (value === 'sms') label = 'SMS';
                            else if (value === 'priority') label = 'Tag as Priority(Notification)';
                            else if (value === 'alert') label = 'Tag as Priority(Alert)';
                            else label = value;
                            return <Chip key={value} label={label} size="small" />;
                          })}
                        </Box>
                      )}
                    >
                      <MenuItem value="in-app">In-App</MenuItem>
                      <MenuItem value="email">Email</MenuItem>
                      <MenuItem value="sms">SMS</MenuItem>
                      <MenuItem value="priority">Tag as Priority(Notification)</MenuItem>
                      <MenuItem value="alert">Tag as Priority(Alert)</MenuItem>
                    </Field>
                  </FormControl>

                  {/* Role & User Selection */}
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
                      Role & User Selection
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Select Roles</InputLabel>
                          <Field
                            name="roles"
                            as={Select}
                            multiple
                            value={values.roles}
                            onChange={(event: SelectChangeEvent<string[]>) => {
                              const {
                                target: { value },
                              } = event;
                              setFieldValue('roles', typeof value === 'string' ? value.split(',') : value);
                            }}
                            displayEmpty
                            renderValue={(selected: string[]) => {
                              return (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {selected.map((value) => {
                                    const role = rolesList.find((r: any) => r.uid === value);
                                    return <Chip key={value} label={role?.name || value} size="small" />;
                                  })}
                                </Box>
                              );
                            }}
                          >
                            {rolesList.map((role: any) => (
                              <MenuItem key={role.uid} value={role.uid}>
                                {role.name}
                              </MenuItem>
                            ))}
                          </Field>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Select Client Users</InputLabel>
                          <Field
                            name="users"
                            as={Select}
                            multiple
                            value={values.users}
                            onChange={(event: SelectChangeEvent<string[]>) => {
                              const {
                                target: { value },
                              } = event;
                              setFieldValue('users', typeof value === 'string' ? value.split(',') : value);
                            }}
                            displayEmpty
                            renderValue={(selected: string[]) => {
                              console.log("selected", selected);
                              return (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {selected.map((value) => {
                                    const user = usersList.find((u: any) => u.uid === value);
                                    return <Chip key={value} label={user?.name || value} size="small" />;
                                  })}
                                </Box>
                              );
                            }}
                          >
                            {usersList.map((user: any) => (
                              <MenuItem key={user.uid} value={user.uid}>
                                {user.name}
                              </MenuItem>
                            ))}
                          </Field>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Box>

                 

                  {/* Save/Cancel */}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, gap: 2 }}>
                    <Button variant="outlined" color="error" disabled={isSubmitting}>
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isSubmitting || !hasNotificationPermission}
                      sx={{ bgcolor: 'secondary.dark', '&:hover': { bgcolor: 'secondary.main' } }}
                    >
                      Save Notification Settings
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Form>
        )}
      </Formik>
    </div></Box>
  );
};

export default TeamNotificationSettings;
